import React, { useCallback, useId, useMemo, useState } from "react"

import {
  isResponseStreamFormatValue,
  RESPONSE_STREAM_FORMAT,
} from "@/constants/agent-response-stream-format"
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import { SimulatorType } from "@/types/api/SimulatorType"
import type { ChatAgentMetadata } from "@/types/backend-contracts"

import {
  AgentTestConnectionButton,
  type AgentTestConnectionConfig,
} from "../AgentTestConnectionButton"
import { SecretInput } from "./SecretInput"
import { TextSimulatorAdvancedSettings } from "./TextSimulatorAdvancedSettings"
import { TEXT_SIMULATOR_TOOLTIPS } from "./textSimulatorTooltips"

interface TextSimulatorConfigProps {
  simulatorType: SimulatorType
  simulationMetadata: Partial<ChatAgentMetadata>
  onMetadataUpdate: (metadata: Partial<ChatAgentMetadata>) => void
  disabled?: boolean
  testConnection?: AgentTestConnectionConfig | null
  hideAdvancedSettings?: boolean
  fieldGroup?: "all" | "primary" | "advanced"
}

// Template examples for JSON fields
const TEMPLATE_EXAMPLES = {
  authorization_header: `your-token-here`,
  initialization_payload: `{
  "session_id": "unique-session-id",
  "config": {
    "temperature": 0.7,
    "max_tokens": 1000
  },
  "user_context": {
    "name": "John Doe",
    "preferences": {}
  }
}`,
  custom_data: `{
  "metadata": {
    "source": "evaluation",
    "version": "1.0"
  },
  "additional_context": {}
}`,
  custom_headers: `{
  "X-Session-ID": "{{sessionId}}",
  "X-Custom-Header": "{{init_response.field}}"
}`,
}

export const TextSimulatorConfig: React.FC<TextSimulatorConfigProps> = ({
  simulatorType,
  simulationMetadata,
  onMetadataUpdate,
  disabled = false,
  testConnection,
  hideAdvancedSettings = false,
  fieldGroup = "all",
}) => {
  const currentMetadata = simulationMetadata

  // Validation states for JSON fields
  const [payloadError, setPayloadError] = useState<string | null>(null)
  const [customDataError, setCustomDataError] = useState<string | null>(null)
  const [customHeadersError, setCustomHeadersError] = useState<string | null>(
    null,
  )
  // Auth header type and value state - reactive to prop changes
  const authHeaderValue = currentMetadata.authorization_header || ""

  // Detect auth type from existing value
  const detectAuthType = useCallback((value: string) => {
    if (value.startsWith("Bearer ")) {
      return "bearer"
    }
    if (value.startsWith("API-Key ") || value.startsWith("X-API-Key ")) {
      return "api-key"
    }
    return "custom"
  }, [])

  // Extract the token/key part (without prefix)
  const getTokenValue = useCallback((value: string, type: string) => {
    if (type === "bearer" && value.startsWith("Bearer ")) {
      return value.substring(7)
    }
    if (type === "api-key" && value.startsWith("API-Key ")) {
      return value.substring(8)
    }
    if (type === "api-key" && value.startsWith("X-API-Key ")) {
      return value.substring(10)
    }
    return value
  }, [])

  // Derived state from props - updates when metadata changes
  const derivedAuthType = useMemo(
    () => detectAuthType(authHeaderValue),
    [authHeaderValue, detectAuthType],
  )
  const derivedTokenValue = useMemo(
    () => getTokenValue(authHeaderValue, derivedAuthType),
    [authHeaderValue, derivedAuthType, getTokenValue],
  )

  // Local state that syncs with derived values
  const [authType, setAuthType] = useState(derivedAuthType)
  const [tokenValue, setTokenValue] = useState(derivedTokenValue)

  // Sync local state when props change (for persistence)
  React.useEffect(() => {
    setAuthType(derivedAuthType)
    setTokenValue(derivedTokenValue)
  }, [derivedAuthType, derivedTokenValue])

  // Handle auth type change
  const handleAuthTypeChange = useCallback(
    (newType: string) => {
      setAuthType(newType)

      let newHeaderValue = ""
      if (newType === "bearer") {
        newHeaderValue = `Bearer ${tokenValue}`
      } else if (newType === "api-key") {
        newHeaderValue = `API-Key ${tokenValue}`
      } else {
        newHeaderValue = tokenValue
      }

      onMetadataUpdate({ authorization_header: newHeaderValue })
    },
    [tokenValue, onMetadataUpdate],
  )

  // Handle token value change
  const handleTokenValueChange = useCallback(
    (newValue: string) => {
      setTokenValue(newValue)

      let newHeaderValue = ""
      if (authType === "bearer") {
        newHeaderValue = `Bearer ${newValue}`
      } else if (authType === "api-key") {
        newHeaderValue = `API-Key ${newValue}`
      } else {
        newHeaderValue = newValue
      }

      onMetadataUpdate({ authorization_header: newHeaderValue })
    },
    [authType, onMetadataUpdate],
  )

  // Helper to validate and update JSON fields
  const validateAndUpdateJson = useCallback(
    (
      fieldName: string,
      value: string,
      setError: (error: string | null) => void,
    ) => {
      if (value.trim() === "") {
        setError(null)
        onMetadataUpdate({ [fieldName]: value })
        return
      }

      try {
        JSON.parse(value)
        setError(null)
      } catch {
        setError("Invalid JSON format - saved as typed")
      }

      onMetadataUpdate({ [fieldName]: value })
    },
    [onMetadataUpdate],
  )

  // Format JSON string for display
  const formatJsonString = useCallback(
    (jsonString: string | object | null | undefined) => {
      if (!jsonString) {
        return ""
      }
      if (typeof jsonString === "object") {
        return JSON.stringify(jsonString, null, 2)
      }
      if (jsonString.trim() === "") {
        return ""
      }
      try {
        const parsed: unknown = JSON.parse(jsonString)
        return JSON.stringify(parsed, null, 2)
      } catch {
        return jsonString
      }
    },
    [],
  )

  // Get formatted values with backward compatibility
  const formattedValues = useMemo(
    () => ({
      initialization_payload: formatJsonString(
        currentMetadata.initialization_payload,
      ),
      custom_data: formatJsonString(currentMetadata.custom_data),
      custom_headers: formatJsonString(currentMetadata.custom_headers),
    }),
    [currentMetadata, formatJsonString],
  )

  // IDs for label/input associations
  const chatEndpointLabelId = useId()
  const chatEndpointInputId = useId()
  const initEndpointLabelId = useId()
  const initEndpointInputId = useId()
  const authHeaderLabelId = useId()
  const authTypeSelectId = useId()
  const authTokenInputId = useId()
  const initPayloadLabelId = useId()
  const customDataLabelId = useId()
  const customHeadersLabelId = useId()
  const responseFormatLabelId = useId()
  const responseFormatSelectId = useId()
  const responseStreamFormatLabelId = useId()
  const responseStreamFormatSelectId = useId()
  const payloadWrapperLabelId = useId()
  const payloadWrapperInputId = useId()

  if (simulatorType !== SimulatorType.TEXT) {
    return null
  }

  return (
    <div className="space-y-4">
      {fieldGroup !== "advanced" && testConnection && (
        <AgentTestConnectionButton
          agentId={testConnection.agentId}
          disabled={testConnection.disabled}
          disabledReason={testConnection.disabledReason}
        />
      )}

      {/* Chat Endpoint (Required) */}
      {fieldGroup !== "advanced" && (
        <div className="space-y-2">
          <FieldLabel
            id={chatEndpointLabelId}
            htmlFor={chatEndpointInputId}
            tooltip={TEXT_SIMULATOR_TOOLTIPS.chatEndpoint}
          >
            Chat Endpoint
          </FieldLabel>
          <Input
            id={chatEndpointInputId}
            value={currentMetadata.chat_endpoint || ""}
            placeholder="Enter your chat endpoint"
            onChange={(e) =>
              onMetadataUpdate({ chat_endpoint: e.target.value })
            }
            disabled={disabled}
            data-testid="chat-endpoint-input"
          />
        </div>
      )}

      {/* Initialization Endpoint (Optional) */}
      {fieldGroup !== "primary" && (
        <div className="space-y-2">
          <FieldLabel
            id={initEndpointLabelId}
            htmlFor={initEndpointInputId}
            tooltip={TEXT_SIMULATOR_TOOLTIPS.initializationEndpoint}
          >
            Initialization Endpoint
          </FieldLabel>
          <Input
            id={initEndpointInputId}
            value={currentMetadata.initialization_endpoint || ""}
            placeholder="Enter your initialization endpoint"
            onChange={(e) =>
              onMetadataUpdate({ initialization_endpoint: e.target.value })
            }
            disabled={disabled}
          />
        </div>
      )}

      {/* Legacy single authorization value remains available in Advanced. */}
      {fieldGroup !== "primary" && (
        <div className="space-y-2">
          <FieldLabel
            id={authHeaderLabelId}
            tooltip={TEXT_SIMULATOR_TOOLTIPS.authorizationHeader}
          >
            Authorization Header
          </FieldLabel>
          <div className="flex gap-2">
            <Select
              value={authType}
              onValueChange={handleAuthTypeChange}
              disabled={disabled}
            >
              <SelectTrigger
                id={authTypeSelectId}
                className="w-40"
                aria-labelledby={authHeaderLabelId}
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bearer" description="OAuth/JWT token">
                  Bearer
                </SelectItem>
                <SelectItem
                  value="api-key"
                  description="API key authentication"
                >
                  API Key
                </SelectItem>
                <SelectItem value="custom" description="Custom format">
                  Custom
                </SelectItem>
              </SelectContent>
            </Select>
            <SecretInput
              id={authTokenInputId}
              revealLabel="authorization token"
              value={tokenValue}
              placeholder="Enter an authorization token"
              onChange={(e) => handleTokenValueChange(e.target.value)}
              disabled={disabled}
              className="flex-1"
              aria-label="Authorization token"
            />
          </div>
        </div>
      )}

      {/* Initialization Payload (JSON) */}
      {fieldGroup !== "primary" && (
        <div className="space-y-2">
          <FieldLabel
            id={initPayloadLabelId}
            tooltip={TEXT_SIMULATOR_TOOLTIPS.initializationPayload}
          >
            Initialization Payload
          </FieldLabel>
          <JsonCodeEditor
            hideGutters
            copyable
            placeholder={
              formattedValues.initialization_payload ||
              TEMPLATE_EXAMPLES.initialization_payload
            }
            value={formattedValues.initialization_payload}
            onChange={(value) =>
              validateAndUpdateJson(
                "initialization_payload",
                value,
                setPayloadError,
              )
            }
            rows={5}
            disabled={disabled}
            error={payloadError}
            ariaLabelledBy={initPayloadLabelId}
            ariaLabel="Initialization payload"
          />
        </div>
      )}

      {/* Custom Data (JSON) */}
      {fieldGroup !== "primary" && (
        <div className="space-y-2">
          <FieldLabel
            id={customDataLabelId}
            tooltip={TEXT_SIMULATOR_TOOLTIPS.customData}
          >
            Custom Data
          </FieldLabel>
          <JsonCodeEditor
            hideGutters
            copyable
            placeholder={
              formattedValues.custom_data || TEMPLATE_EXAMPLES.custom_data
            }
            value={formattedValues.custom_data}
            onChange={(value) =>
              validateAndUpdateJson("custom_data", value, setCustomDataError)
            }
            rows={5}
            disabled={disabled}
            error={customDataError}
            ariaLabelledBy={customDataLabelId}
            ariaLabel="Custom data"
          />
        </div>
      )}

      {/* General headers are the main Chat authentication configuration. */}
      {fieldGroup !== "advanced" && (
        <div className="space-y-2">
          <FieldLabel
            id={customHeadersLabelId}
            tooltip={TEXT_SIMULATOR_TOOLTIPS.customHeaders}
          >
            Headers
          </FieldLabel>
          <JsonCodeEditor
            hideGutters
            copyable
            placeholder={
              formattedValues.custom_headers || TEMPLATE_EXAMPLES.custom_headers
            }
            value={formattedValues.custom_headers}
            onChange={(value) =>
              validateAndUpdateJson(
                "custom_headers",
                value,
                setCustomHeadersError,
              )
            }
            rows={4}
            disabled={disabled}
            error={customHeadersError}
            ariaLabelledBy={customHeadersLabelId}
            ariaLabel="Headers"
          />
        </div>
      )}

      {/* Response Format and Payload Wrapper - Side by Side */}
      <div
        className={
          fieldGroup === "all"
            ? "grid grid-cols-1 md:grid-cols-2 gap-4"
            : "space-y-4"
        }
      >
        {/* Response Format (Dropdown) */}
        {fieldGroup !== "advanced" && (
          <div className="space-y-2">
            <FieldLabel
              id={responseFormatLabelId}
              htmlFor={responseFormatSelectId}
              tooltip={TEXT_SIMULATOR_TOOLTIPS.responseFormat}
            >
              Response Format
            </FieldLabel>
            <Select
              value={currentMetadata.response_format || "chat_completions"}
              onValueChange={(value) => {
                if (value) {
                  onMetadataUpdate({ response_format: value })
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger
                id={responseFormatSelectId}
                aria-labelledby={responseFormatLabelId}
              >
                <SelectValue placeholder="Select Response Format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  value="chat_completions"
                  description="Standard OpenAI format for chat completions"
                >
                  ChatCompletions (Default)
                </SelectItem>
                <SelectItem
                  value="responses_api"
                  description="Function call output format for advanced use cases"
                >
                  Responses API
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Payload Wrapper (Optional) */}
        {fieldGroup !== "primary" && (
          <div className="space-y-2">
            <FieldLabel
              id={payloadWrapperLabelId}
              htmlFor={payloadWrapperInputId}
              tooltip={TEXT_SIMULATOR_TOOLTIPS.payloadWrapper}
            >
              Payload Wrapper Field
            </FieldLabel>
            <Input
              id={payloadWrapperInputId}
              value={currentMetadata.payload_wrapper || ""}
              placeholder="Enter the response wrapper field"
              onChange={(e) =>
                onMetadataUpdate({
                  payload_wrapper: e.target.value.trim() || null,
                })
              }
              disabled={disabled}
            />
          </div>
        )}
      </div>

      {/* Response Stream Format (Dropdown) */}
      {fieldGroup !== "primary" && (
        <div className="space-y-2">
          <FieldLabel
            id={responseStreamFormatLabelId}
            htmlFor={responseStreamFormatSelectId}
            tooltip={TEXT_SIMULATOR_TOOLTIPS.responseStreamFormat}
          >
            Response Stream Format
          </FieldLabel>
          <Select
            value={
              currentMetadata.response_stream_format ||
              RESPONSE_STREAM_FORMAT.NONE
            }
            onValueChange={(value) => {
              if (isResponseStreamFormatValue(value)) {
                onMetadataUpdate({
                  response_stream_format: value,
                })
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger
              id={responseStreamFormatSelectId}
              aria-labelledby={responseStreamFormatLabelId}
            >
              <SelectValue placeholder="Select Response Stream Format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value={RESPONSE_STREAM_FORMAT.NONE}
                description="The endpoint returns a single buffered JSON response"
              >
                None / Buffered JSON (Default)
              </SelectItem>
              <SelectItem
                value={RESPONSE_STREAM_FORMAT.AI_SDK_DATA_STREAM}
                description="The endpoint streams Server-Sent Events in the Vercel AI SDK UI Message Stream format"
              >
                Vercel AI SDK (SSE stream)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {!hideAdvancedSettings && fieldGroup === "all" && (
        <TextSimulatorAdvancedSettings
          metadata={currentMetadata}
          onMetadataUpdate={onMetadataUpdate}
          disabled={disabled}
        />
      )}
    </div>
  )
}
